import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

interface PlannedChangesChartProps {
  plannedToday: number;
  plannedThisWeek: number;
  emergencyChanges: number;
  onBarClick?: (filterType: string) => void;
}

const PlannedChangesChart: React.FC<PlannedChangesChartProps> = ({
  plannedToday,
  plannedThisWeek,
  emergencyChanges,
  onBarClick
}) => {
  const labels = ['Planned Today', 'Planned This Week', 'Emergency'];
  const values = [plannedToday, plannedThisWeek, emergencyChanges];
  const colors = [
    'rgba(59, 130, 246, 0.8)',   // blue - today
    'rgba(16, 185, 129, 0.8)',   // green - this week
    'rgba(239, 68, 68, 0.8)',    // red - emergency
  ];
  const filterTypes = ['plannedToday', 'plannedThisWeek', 'emergency'];

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Change Requests',
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 8,
        barThickness: 60,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.raw} change requests`
        }
      },
      datalabels: {
        display: true,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 16,
        },
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number) => value,
      }
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { precision: 0 }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        onBarClick(filterTypes[index]);
      }
    },
  };

  return (
    <div id="chart-planned-changes" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Planned & Emergency Changes</h3>
        <span className="text-xs text-gray-500">Click to view details</span>
      </div>
      <div className="h-64">
        <Bar data={chartData} options={options} />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-4 text-center">
        <button
          onClick={() => onBarClick?.('plannedToday')}
          className="p-3 rounded-lg bg-blue-50 border-l-4 border-blue-500 hover:bg-blue-100 transition-colors"
        >
          <div className="text-2xl font-bold text-blue-600">{plannedToday}</div>
          <div className="text-xs text-gray-600">Planned Today</div>
        </button>
        <button
          onClick={() => onBarClick?.('plannedThisWeek')}
          className="p-3 rounded-lg bg-green-50 border-l-4 border-green-500 hover:bg-green-100 transition-colors"
        >
          <div className="text-2xl font-bold text-green-600">{plannedThisWeek}</div>
          <div className="text-xs text-gray-600">This Week</div>
        </button>
        <button
          onClick={() => onBarClick?.('emergency')}
          className="p-3 rounded-lg bg-red-50 border-l-4 border-red-500 hover:bg-red-100 transition-colors"
        >
          <div className="text-2xl font-bold text-red-600">{emergencyChanges}</div>
          <div className="text-xs text-gray-600">Emergency</div>
        </button>
      </div>
    </div>
  );
};

export default PlannedChangesChart;
